/* eslint-disable react/jsx-props-no-spreading */
import { History, Location } from 'history';
import { useRef, useMemo, memo } from 'react';
import { useSelector } from 'react-redux';
import type { NextRouter } from 'next/router';
import type { useRouter } from 'next/navigation';
import ErrorBoundary from './ErrorBoundary';
import {
  generateVisibilityClassNames,
} from '../../../../utils/Utils';
import FilterEmptyResult from './Filters/FilterEmptyResult';
import Analytics from './Analytics';
import { Component } from '../../types/schema';
import { getUserDetails } from '../../store/selectors/user.selectors';
import { getProductDetails } from '../../store/selectors/product.selectors';
import { getActiveTieredSale } from '../../store/selectors/cart.selectors';
import LoadingContainer from './Loading';

import {
  componentMap, loadNextAsyncComponent, loadLoadableAsyncComponent,
} from './ComponentsLibrary';
import { loadSyncComponent } from '../../../../../next-ssg/loadComponent';
import { getFilterID, getIsPageDataPending } from '../../store/selectors/page.selectors';
import { getIsXLWidth } from '../../store/selectors/ui.selectors';

type GraphCMSComponentsProps = {
  nextApp?: boolean;
  ssg?: boolean;
  components?: Component[];
  header?: string;
  history?: History;
  location?: Location | NextRouter | ReturnType<typeof useRouter>;
  userDetails?: ReturnType<typeof getUserDetails>;
  tieredCoupon?: ReturnType<typeof getActiveTieredSale>;
  productDetails?: ReturnType<typeof getProductDetails>;
};

interface MappedComponentProps extends Omit<GraphCMSComponentsProps, 'components'> {
  component: Component;
}

export function toggleAccordion(
  this: React.Component<
  { header: string; userDetails?: ReturnType<typeof getUserDetails>; },
  { openToggle: boolean }
  >,
) {
  const openToggle = !this.state.openToggle;
  this.setState({
    openToggle,
  });
  try {
    const {
      header: source = '',
      userDetails: {
        email = '',
      } = {},
    } = this.props;

    Analytics.enqueue({
      method: 'track',
      params: {
        event: 'PDP Below The Fold Accordion',
        properties: {
          action: openToggle ? 'Opened' : 'Closed',
          source,
          email,
        },
      },
      location: Analytics.getLocation(),
    });
  } catch (e) { /* nothing to do */ }
}

interface LoadComponentOptions {
  ssg?: boolean;
  nextApp?: boolean;
}

export function loadComponent<K>(
  componentName: K extends keyof typeof componentMap ? K : never,
  options: LoadComponentOptions = {},
) {
  const { nextApp, ssg } = options;

  if (nextApp) {
    return ssg
      ? loadSyncComponent(componentName)
      : loadNextAsyncComponent(componentName);
  }

  return loadLoadableAsyncComponent(componentName);
}

const MappedComponent = memo(({
  component, nextApp, ssg, ...restOfProps
}: MappedComponentProps) => {
  if (component.type in componentMap) {
    const componentName = component.type as keyof typeof componentMap;

    const TagName = loadComponent(componentName, { nextApp, ssg });

    return (
      <ErrorBoundary
        key={component.id}
        errorMessage="Could not load component, try again."
      >
        <div className={generateVisibilityClassNames(component.visibilities)}>
          <TagName
            {...component}
            {...restOfProps}
            nextApp={nextApp}
            toggleAccordion={toggleAccordion}
          />
        </div>
      </ErrorBoundary>
    );
  }

  return null;
});

MappedComponent.displayName = 'GraphCMSComponentsMappedComponent';

const GraphCMSComponents = ({
  nextApp = false,
  ssg = false,
  components = [],
  ...restOfProps
}: GraphCMSComponentsProps) => {
  const filterID = useSelector(getFilterID);
  const isDesktop = useSelector(getIsXLWidth);
  const filtersRef = useRef({ clear: () => {} });
  const isPending = useSelector(getIsPageDataPending);
  let columnFilterContent = null;
  const Filters = useMemo(
    () => loadComponent('Filters', { nextApp, ssg }),
    [nextApp, ssg],
  );

  if (filterID && isDesktop) {
    const firstCollectionIdx = components.findIndex(
      ({ type }) => type === 'ProductCollection',
    );

    if (isPending) {
      columnFilterContent = <LoadingContainer />;
    } else if (firstCollectionIdx === -1) {
      columnFilterContent = <FilterEmptyResult onClear={filtersRef.current.clear}/>;
    } else {
      columnFilterContent = components
        .slice(firstCollectionIdx)
        .map((component, idx) => {
          const collectionKey = component.collections?.[0]?.id;
          const key = component.type === 'ProductCollection' && collectionKey ? collectionKey : (component.id || `${idx}-component`);
          return (
            <MappedComponent
              key={key}
              nextApp={nextApp}
              ssg={ssg}
              component={component}
              {...restOfProps}
            />
          );
        });
    }

    return (
      <>
        {components.slice(0, firstCollectionIdx)
          .map((component, idx) => (
            <MappedComponent
              nextApp={nextApp}
              ssg={ssg}
              key={component.id || `${idx}-component`}
              component={component}
              {...restOfProps}
            />
          ))}
        <div className="collections-with-filter">
          <div className="column-filter">
            {
              <Filters updateOnChange ref={filtersRef} />
            }
          </div>
          <div className="column-collections">
            {columnFilterContent}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {components.map((component, idx) => (
        <MappedComponent
          nextApp={nextApp}
          ssg={ssg}
          key={component.id || `${idx}-component`}
          component={component}
          {...restOfProps}
        />
      ))}
    </>
  );
};

export default GraphCMSComponents;
